import React, { useEffect, useState } from 'react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import ResyncProgress from './ResyncProgress';
import '../shared/ConfirmModal.css';

// BULK RESYNC BY THE CURRENT FILTERS — re-query OHIP for every resyncable record the Logs screen's filters select,
// across every hotel and pipeline implementation. The modal inherits the page's own filters (so it offers the same
// search terms as the page it came from) and resyncs exactly what the list shows — the same way Purge deletes exactly
// what the list shows. One resync run is started per (implementation, kind); the shared ResyncProgress then shows
// per-hotel progress: hotel · kind · record count · live processing status.
//
// `filters`: the page's server query (integrationId, hotelId, system, module, step, outcome, q, transactionId,
//   since, until). `scope`: those same filters in words. Superadmin only (the button is hidden too).

export default function BulkStatusResyncModal({ filters, scope, onClose }) {
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [runs, setRuns] = useState(null);   // null before start; after: the server's runs

    const start = async () => {
        setError(null);
        setStarting(true);
        try {
            const res = await integrationsService.resyncLogByStatus(filters);
            const started = res.runs || [];
            setRuns(started);
            setStarting(false);
            if (!started.length) setError('No re-queryable records (reservations, profiles or blocks) match these filters.');
        } catch (e) {
            setStarting(false);
            setError(apiErrorMessage(e, 'The resync could not be started.'));
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog cm-dialog-wide" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <h2 className="cm-title">Resync the filtered records</h2>

                {!runs ? (
                    <>
                        <p className="cm-message">
                            Re-query OHIP for every resyncable record the current filters select — across every hotel and
                            pipeline implementation that has one. Each record is read fresh from OHIP (not a replay of what
                            arrived before) and sent through its pipeline again, to live targets. One resync run is started
                            per implementation and kind.
                        </p>

                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem 1rem' }}>
                            <div style={{ fontWeight: 600, color: '#111827', marginBottom: '0.3rem' }}>What will be resynced</div>
                            <div style={{ fontSize: '0.85rem', color: '#374151' }}>Every record for {scope}.</div>
                            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.4rem' }}>
                                These are the Logs screen's current filters. To change what is resynced, close this and
                                adjust the filters on the page, then reopen. Only reservations, profiles and blocks can be
                                resynced.
                            </div>
                        </div>

                        {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Cancel</button>
                            <button className="cm-btn-confirm" onClick={start} disabled={starting}>
                                {starting ? 'Starting…' : 'Start resync'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <ResyncProgress runs={runs} noun="resyncs" />

                        {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Close</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
